<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import markdownit from 'markdown-it';

const { t } = useI18n();

const md = markdownit({ linkify: true });

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    token.attrPush(['target', '_blank']);
    token.attrPush(['class', 'link']);
    return self.renderToken(tokens, idx, options);
};

// Props
const props = defineProps({
    dualLanguageInput: {
        type: Object,
        default: () => ({})
    }
});

// Emits
const emit = defineEmits(['save', 'close']);

// Refs
const inputJa = ref(null);
const translating = ref(false);
const saving = ref(false);
const manuallyEnteredAnilistId = ref(false);
const validatingAnilistId = ref(false);

// Computed
const text = computed(() => {
    return md.render(props.dualLanguageInput.text);
});

const checkTranslation = async () => {
    // Full romaji (ex: studio names, non-Japanese)
    if (props.dualLanguageInput.ja && !props.dualLanguageInput.en && /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žƀ-ɏḀ-ỿ0-9 .,:;!?()\[\]{}'"’‘“”\/\\_\-°•・･℃@＠#$☆△▲%&*+=^|]+$/g.test(props.dualLanguageInput.ja)) {
        [props.dualLanguageInput.ja, props.dualLanguageInput.en] = [props.dualLanguageInput.en, props.dualLanguageInput.ja];
    }

    if ((props.dualLanguageInput.ja && !props.dualLanguageInput.en) || (!props.dualLanguageInput.ja && props.dualLanguageInput.en)) {
        translateStaff();
    }
};

const translateStaff = async () => {
    translating.value = true;

    const fakeStaff = {
        scope: "local",
        ...(props.dualLanguageInput.ja && { ja: props.dualLanguageInput.ja }),
        ...(props.dualLanguageInput.en && { en: props.dualLanguageInput.en }),
        ...(props.dualLanguageInput.mode === "studio" && { isStudio: true }),
        callback: (value) => {
            console.log("Callback", value);
            
            translating.value = false;

            // Save if already saving
            if (saving.value) {
                save();
            }

            if (!fakeStaff.en) {
                return;
            }
            
            props.dualLanguageInput.en = fakeStaff.en || "";
            
            if (fakeStaff.id) {
                props.dualLanguageInput.anilistId = fakeStaff.id;
            }
        }
    };
    
    const NameService = (await import('@/services/NameService')).default;
    NameService.addToTranslationQueue(fakeStaff, "forced", "top");

    console.log(fakeStaff);
};

const handleKeydown = (e) => {
    if (e.key === 'Escape') {
        close();
    }
};

const handleAnilistIdInput = () => {
    manuallyEnteredAnilistId.value = true;
};

const save = async () => {
    if (validatingAnilistId.value) {
        return;
    }

    if (document.activeElement) {
        document.activeElement.blur();
    }

    await new Promise(resolve => setTimeout(resolve, 50));

    console.log("Trying to save...", saving.value);

    // Exit
    if (!translating.value) {
        saving.value = false;

        if (manuallyEnteredAnilistId.value && props.dualLanguageInput.anilistId) {
            validatingAnilistId.value = true;

            try {
                const NameService = (await import('@/services/NameService')).default;
                const isValid = await NameService.validateID(
                    props.dualLanguageInput.anilistId,
                    props.dualLanguageInput.mode === "studio"
                );

                if (!isValid) {
                    return;
                }
            } finally {
                validatingAnilistId.value = false;
            }
        }

        emit('save', props.dualLanguageInput.ja, props.dualLanguageInput.en, props.dualLanguageInput.anilistId);
        close();

        return;
    }

    saving.value = true;
};

const remove = async () => {
    if (translating.value) {
        return;
    }

    props.dualLanguageInput.ja = "";
    props.dualLanguageInput.en = "";
    props.dualLanguageInput.anilistId = null;

    save();
};

const close = () => {
    emit('close');
};

// Lifecycle
onMounted(() => {
    document.addEventListener('keydown', handleKeydown);

    nextTick(() => {
        if (inputJa.value) {
            inputJa.value.focus();
        }
    });
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
    <!-- Fond -->
    <div
        class="fixed top-0 left-0 w-full h-full bg-black/50 flex-center"
        :style="{ zIndex: dualLanguageInput.zIndex || 50 }"
    >
        <!-- Dialog -->
        <Transition appear enter-from-class="scale-95 opacity-0" enter-to-class="scale-100 opacity-100">
            <div
                class="dialog bg-app-surface1 rounded-xl overflow-hidden md:min-w-[360px] md:w-[40%] w-[90%] max-w-[600px] shadow-2xl max-h-[90vh] overflow-y-auto transition-all duration-100 ease-out flex flex-col"
                role="dialog"
                aria-modal="true"
                :aria-label="dualLanguageInput.title || t('common.save')"
            >
            <!-- Header avec titre et X -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-app-border">
                <h2 v-if="dualLanguageInput.title" class="text-lg font-semibold text-app-text">{{ dualLanguageInput.title }}</h2>
                <div v-else />
                <button
                    type="button"
                    class="inline-flex items-center justify-center w-8 h-8 rounded-full text-app-text-soft hover:bg-app-border transition-colors"
                    @click="close"
                    :aria-label="t('common.close')"
                    :title="t('common.close')"
                >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Contenu -->
            <div class="flex-1 px-6 py-5">
                <p class="text-left break-words leading-relaxed text-app-text2" v-html="text"></p>
            </div>

            <div class="px-6 pb-5 space-y-4">
                <input
                    ref="inputJa"
                    class="w-full input"
                    :class="{ 'in-queue enabled:bg-blue-100': translating }"
                    :placeholder="translating ? t('staffComponent.placeholders.translating') : t('staffComponent.placeholders.kanji')"
                    :maxlength="dualLanguageInput.maxlength || 200000"
                    v-model.trim="dualLanguageInput.ja"
                    @change="checkTranslation"
                    @keydown.ctrl.enter="save"
                />
                <input
                    class="w-full input"
                    :class="{ 'in-queue enabled:bg-blue-100': translating }"
                    :placeholder="translating ? t('staffComponent.placeholders.translating') : t('staffComponent.placeholders.romaji')"
                    :maxlength="dualLanguageInput.maxlength || 200000"
                    v-model.trim="dualLanguageInput.en"
                    @change="checkTranslation"
                    @keydown.ctrl.enter="save"
                />
                <input
                    class="w-full input"
                    :class="{ 'in-queue enabled:bg-blue-100': translating }"
                    :placeholder="translating ? t('staffComponent.placeholders.translating') : `ID`"
                    :maxlength="dualLanguageInput.maxlength || 200000"
                    v-model.trim="dualLanguageInput.anilistId"
                    :disabled="validatingAnilistId"
                    @input="handleAnilistIdInput"
                    @keydown.ctrl.enter="save"
                />
                <p class="w-full text-left text-gray-400 text-xs" v-if="dualLanguageInput.mode === 'staff'">
                    The ID will not be deleted when clicking the "Delete" button.
                </p>
            </div>

            <!-- Footer -->
            <div class="flex flex-col gap-2 pt-3 px-6 py-6">
                <div class="flex gap-2 justify-end">
                    <div class="flex-1">
                        <button type="button" class="btn btn-cancel !rounded-full" @click="close">{{ t('common.cancel') }}</button>
                    </div>
                    <button type="button" class="btn btn-danger !rounded-full" @click="remove">
                        <font-awesome-icon icon="fa-solid fa-trash" />
                        {{ translating ? t('staffComponent.placeholders.translating') : t('common.delete') }}
                    </button>
                    <button type="button" class="btn btn-primary !rounded-full" :disabled="validatingAnilistId" @click="save">
                        <font-awesome-icon icon="fa-solid fa-check" />
                        {{ translating ? t('staffComponent.placeholders.translating') : t('common.save') }}
                    </button>
                </div>
            </div>
            </div>
        </Transition>
    </div>
</template>